import React, { memo, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import LogisticsTable from './LogisticsTable';
import LogisticsSummary from "./LogisticsSummary";
import {
	setSearchText, setLogisticsDetail, setLogisticsDiscard,
	updatePayload, bulkEditLogisticsLoad, setBulkEdit,
} from '../../actions/logistics';
import { calculateEgLoadDistance, saveEquipmentGroupRates } from "../../actions/equipment-group";
import { formatNumber, Unit } from '../../helpers/unit';
import { SubNav } from '../layout/submenu';
import { ButtonGroupToggle, Icon, Modal } from '../widgets';
import TareLoadHistory from './tareLoads/TareLoadHistory';
import AddLoadForm from './AddLoadForm';
import { isToday, usePermission, getTimeDiffrence, isDeveloper } from '../../helpers/common';
import { hideOverlay, showOverlay } from "../../actions/toast";
import { LOGISTIC_PLAN_TYPE_ID } from "../../constants";
import { setIsLoadHistoryReload } from '../../actions/load';

const LogisticsBody = (props) => {
	const { isRequesting, loads, getFilteredData, farmId, getLogisticsTareHistory, tareLoadHistory, setSelectedViewType, selectedViewType, getLogisticsHistorylist } = props;
	const dispatch = useDispatch();
	const { enableBulkEdit, selectedLoads } = useSelector(state => state.logistics.logisticsHistory);
	const { searchText } = useSelector(state => state.logistics);
	const { filterIds } = useSelector(state => state.logistics.logisticsHistory);
	const searchParams = useSelector(state => state.analyze.searchParams);

	const unit = Unit.getInstance();
	const [seedData, setSeedData] = useState(loads);
	const [saveText, setSaveText] = useState('Save');
	const [unknownColumns, setUnknownColumns] = useState([]);
	const [showAddLoad, setShowAddLoad] = useState(false);
	const [confirmModal, setConfirmModal] = useState({
		rateSchedule: false,
		distanceUpdate: false
	});
	const isDevUser = isDeveloper();

	const allow = usePermission();
	const { range, custom } = searchParams;
	let isInHours = getTimeDiffrence(custom, "hours");
	isInHours = (isInHours > 24);

	const handleConfirmModal = (type, show) => setConfirmModal({ ...confirmModal, [type]: show });

	const tabs = [
		{ value: 1, label: "Logistics History" },
		{ value: 2, label: "Logistics Summary" },
		{ value: 3, label: "Tare History" }
	];

	useEffect(() => {
		let seedLoads = loads;
		let unknowns = [];

		// Remove Unknown
		let { header, body, isRequesting } = seedLoads['seedHistory'];

		const seedHistoryHeaders = header.filter(e => e.key !== 'action').map((e) => e.filter);
		const filteredList = filterIds && Object.keys(filterIds).filter(id => filterIds[id] ? filterIds[id].length !== 0 : null);

		if (!isRequesting && body && body.length > 0) {
			for (const seedHistoryHeader of seedHistoryHeaders) {
				let data = body.filter((e) => e[seedHistoryHeader] !== undefined && e[seedHistoryHeader] !== null && e[seedHistoryHeader] !== "Unknown" && e[seedHistoryHeader] !== " ").map((e) => e[seedHistoryHeader]);

				if (data.length === 0 && !filteredList.includes(seedHistoryHeader)) {
					unknowns.push(seedHistoryHeader);
					const findIndex = header.findIndex(e => e.filter === seedHistoryHeader);

					header[findIndex] = {
						...header[findIndex],
						isShow: false,
					}
					body = body.map((e) => {
						delete e[seedHistoryHeader];
						return e
					})
				}
				else {
					const findIndex = header.findIndex(e => e.filter === seedHistoryHeader);
					header[findIndex] = {
						...header[findIndex],
						isShow: true,
					}
				}
			}
			setUnknownColumns(unknowns);
		}

		setSeedData(seedLoads);
	}, [loads, isRequesting]);

	useEffect(() => {
		const { body } = seedData.seedHistory;
		const totalCapacity = body.reduce((capacity, item) => capacity + item.load_capacity, 0);

		dispatch(setLogisticsDetail({
			totalCapacity: formatNumber(unit.toRoundOfWeight(totalCapacity, 2)),
			totalCount: body && body.length
		}));
	}, [seedData]);

	const handleBulkSave = () => {
		if (selectedLoads && selectedLoads.length > 0) {
			setSaveText('Saving...');
			let params = [];
			loads.seedHistory.body.forEach(load => {
				if (selectedLoads.includes(load.load_activity_uuid)) {
					params.push({
						load_activity_uuid: load.load_activity_uuid,
						crew_name: load.crew_name,
						crew_uuid: load.crew_uuid,
						supplier: load.supplier ? load.supplier : null,
						seed_lot: load.seed_lot ? load.seed_lot : null,
						variety_name: load.variety_name ? load.variety_name : null,
						field_geo_shape_name: load.field_geo_shape_name,
						field_geo_shape_uuid: load.field_geo_shape_uuid,
						field_geo_region_name: load.field_geo_region_name,
						field_geo_region_uuid: load.field_geo_region_uuid,
						source_field_name: load.source_field_name,
						source_field_uuid: load.source_field_uuid,
						offload_geo_shape_name: load.offload_geo_shape_name,
						offload_geo_shape_uuid: load.offload_geo_shape_uuid,
						offload_geo_region_name: load.offload_geo_region_name,
						offload_geo_region_uuid: load.offload_geo_region_uuid,
						offload_geo_storage_location_name: load.offload_geo_storage_location_name,
						offload_geo_storage_location_uuid: load.offload_geo_storage_location_uuid,
						load_capacity: load.load_capacity
					});
				}
			});
			dispatch(bulkEditLogisticsLoad({ farm_uuid: farmId, loads: params }, (isSaved) => {
				if (isSaved) {
					dispatch(updatePayload());
					dispatch(setBulkEdit(false));
					setSaveText('Save');
				} else {
					dispatch(setBulkEdit(false));
					setSaveText('Save');
				}
			}));
		}
	};

	const handleDiscard = () => {
		dispatch(setBulkEdit(false));
		dispatch(setLogisticsDiscard());
	};

	const handleGenerate = (type) => {
		dispatch(showOverlay({ text: "Updating..." }));
		const params = {
			farm_uuid: farmId,
			from_date: custom.from,
			to_date: custom.to,
			range_type: 'custom',
			plan_type_id: LOGISTIC_PLAN_TYPE_ID
		}

		if (type === "rateSchedule") dispatch(saveEquipmentGroupRates(params));
		else dispatch(calculateEgLoadDistance(params));

		setTimeout(() => {
			dispatch(hideOverlay());
			dispatch(setIsLoadHistoryReload(true));
		}, 6000);
	}

	const { body } = seedData['seedHistory'];

	return (
		<>
			<Col className='col-12 seed-body px-4'>
				<Row>
					<Col className='col-5 mt-3'>
						<Row>
							<Col className='col-8 p-0'>
								<SubNav.SearchBox
									placeholder="Search for crew, equipment group, operator, field, offload site"
									onChange={e => dispatch(setSearchText((e.target.value).trim()))}
									value={searchText}
									onClear={() => {
										getFilteredData(true);
										dispatch(setSearchText(""));
									}}
								/>
							</Col>
							<Col className='col-4 p-0'>
								<Button className='btn-sm' variant='primary' onClick={() => getFilteredData()}>Search</Button>
							</Col>
						</Row>
					</Col>
					<Col>
						<ButtonGroupToggle
							options={tabs}
							value={selectedViewType} onSelect={(type) => setSelectedViewType(type)}
							className="btn-group-toggle seed-toggle mt-4" />
					</Col>
					<Col style={{ textAlign: 'end', marginTop: '1%' }}>
						{selectedViewType === 1 &&
							<>
								{!enableBulkEdit ? <Button
									variant='warning'
									className='btn-sm mx-2'
									disabled={isRequesting || (body ? body.length === 0 : true)}
									onClick={() => dispatch(setBulkEdit(!enableBulkEdit))}
								>
									<Icon
										className='fa fa-edit mx-1' />
									Bulk Edit
								</Button> :
									<>
										<Button
											className='btn-sm mx-2'
											variant='primary'
											disabled={selectedLoads && selectedLoads.length === 0}
											onClick={() => handleBulkSave()}
										>
											<Icon
												className='fa fa-floppy-o mr-1'
											/>
											{saveText}
										</Button>
										<Button
											className='btn-sm '
											variant='danger'
											onClick={() => handleDiscard()}
										>
											<Icon
												className='fa fa-times-circle mr-1'
											/>
											Discard
										</Button>
									</>}
							</>}
						{(isToday(range) && allow("load-history", "create")) && (!enableBulkEdit) &&
							(selectedViewType === 1 || selectedViewType === 3) ?
							<>
								<Button
									size="sm"
									variant='success'
									onClick={() => setShowAddLoad(true)}>
									<i className="fa fa-plus mr-2" />
									Add {selectedViewType === 1 ? 'Load' : 'Tare Load'}
								</Button>
								<AddLoadForm
									farmId={farmId}
									show={showAddLoad}
									setShowAddLoad={setShowAddLoad}
									isCloned={false}
									load={null}
									getLogisticsTareHistory={getLogisticsTareHistory}
									getLogisticsHistorylist={getLogisticsHistorylist}
									selectedViewType={selectedViewType}
									onHide={() => setShowAddLoad(false)} />
							</> : null}
						{(isDevUser && selectedViewType === 1 && !enableBulkEdit && !isRequesting
							&& !isInHours && body && body.length > 0) &&
							<Button
								size="sm"
								variant="info"
								disabled={false}
								className={`ml-2`}
								onClick={() =>
									handleConfirmModal("rateSchedule", true)
								}
							>Calculate Rates</Button>
						}
						{(isDevUser && selectedViewType === 1 && !enableBulkEdit && !isRequesting
							&& !isInHours && body && body.length > 0) &&
							<Button
								size="sm"
								variant="info"
								disabled={false}
								className={`ml-2`}
								onClick={() =>
									handleConfirmModal("distanceUpdate", true)
								}
							>Calculate Travelled Distance</Button>
						}
					</Col>
				</Row>

				<Row className={`mt-3 px-0 seedHistory_table ${selectedViewType === 1 ? "show" : "hide"}`}>
					<LogisticsTable
						isRequesting={isRequesting}
						data={seedData}
						unknownColumns={unknownColumns}
					/>
				</Row>

				<Row className={`mt-3 ${selectedViewType === 2 ? "show" : "hide"}`}>
					<LogisticsSummary
						isRequesting={isRequesting}
						data={seedData}
					/>
				</Row>

				<Row className={`mt-3 px-0 ${selectedViewType === 3 ? "show" : "hide"}`}>
					<TareLoadHistory
						data={loads}
						getLogisticsTareHistory={getLogisticsTareHistory}
						tareLoadList={tareLoadHistory}
					/>
				</Row>
			</Col>

			{confirmModal.rateSchedule ? <Modal.Confirm
				className="modal-bordered"
				show={confirmModal.rateSchedule}
				onConfirm={() => handleGenerate("rateSchedule")}
				onHide={() => handleConfirmModal("rateSchedule", false)}
				iconClassName="fa fa-question"
				title={{ content: "Confirmation" }}
				content={`Do you want to calculate rates for all crews?`}
				variant="info"
				confirmButton={{ variant: "info" }}
			/> : null}

			{confirmModal.distanceUpdate ? <Modal.Confirm
				className="modal-bordered"
				show={confirmModal.distanceUpdate}
				onConfirm={() => handleGenerate("distanceUpdate")}
				onHide={() => handleConfirmModal("distanceUpdate", false)}
				iconClassName="fa fa-question"
				title={{ content: "Confirmation" }}
				content={`Do you want to calculate the travelled distance?`}
				variant="info"
				confirmButton={{ variant: "info" }}
			/> : null}
		</>

	);
};

export default memo(LogisticsBody);